/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { EstimateCostDatabaseItemsGridNew as EstimateCostDatabaseItems } from "@Features/estimation";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { itemListSources } from "@Actions/estimation";
import { getDefaultCurrencyDetailForProjectLevel } from "@Actions/Currency";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { CurrencyDetail, SymbolDetail } from "@ServerTypes/currency/currency.schema";

/** The BOQ grid's imperative handle (`TreeGridRef`), taken from the component rather than restated. */
type BoqGridHandle = NonNullable<React.ComponentRef<typeof EstimateCostDatabaseItems>>;

/**
 * A selected BOQ row, as the grid's own `dataView` hands it back. `getAllSelectedItems()` is
 * declared `Array<row | undefined>` because it can hand back holes (see the handle's own comment and
 * `handleLinkBoqItemsClick` below), so the hole is stripped here — this alias names a row that is
 * actually present, and the callers below re-add `| undefined` where they still have to cope with it.
 */
type BoqSelectionRow = NonNullable<ReturnType<BoqGridHandle["dataView"]["getAllSelectedItems"]>[number]>;

/** One BOQ item as this modal hands it back to whichever caller opened it. */
export interface LinkedBoqItem {
  /**
   * The quantity to link: the row's `availableQuantity` when auto-populating, otherwise the user's
   * entry. `number | undefined` rather than `number` because `availableQuantity` is optional on the
   * grid row, so the auto-populate branch really can yield `undefined`.
   */
  customQuantity: number | undefined;
  itemId: ObjectIdString;
  parentsList?: BoqSelectionRow["parentsList"];
}

/** The project currency pair returned by `getDefaultCurrencyDetailForProjectLevel`; `{}` until loaded. */
interface ProjectCurrencyState {
  currencyDetail?: CurrencyDetail;
  symbolDetail?: SymbolDetail;
}

interface LinkBoqToTaskAndFormProps {
  showBoqModal: boolean;
  setShowBoqModal: (state: boolean) => void;
  /** Which caller opened the modal — the Gantt task linker or the form Quantities section. */
  source: "gantt" | "forms";
  addBoqItemsCallBack: (args: { boqItems: LinkedBoqItem[]; autoPopulateState?: boolean }) => void;
  contractId?: string;
  formId?: ObjectIdString;
  selectedTask?: { originalTaskId?: ObjectIdString };
  /** Passed by the Quantities section but not read here; declared so that call site type-checks. */
  formResourceType?: string;
  /** Passed by the Gantt caller but not read here; declared so that call site type-checks. */
  successCallback?: () => void;
}

function LinkBoqToTaskAndForm(props: Readonly<LinkBoqToTaskAndFormProps>) {
  const { t } = useTranslation();
  // Route params enter from outside the type system; the backend route schema validates these ids.
  const params = useParams<{ projectId: ObjectIdString; contractId: ObjectIdString }>();
  const boqItemsRef = useRef<React.ComponentRef<typeof EstimateCostDatabaseItems>>(null);
  const dispatch = useDispatch();
  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  // `contractDetailSchema` declares `linkedEstimateVersion` required and the slice starts life as
  // `{} as ContractDetailsState`, but a contract simply has no linked estimate until one is attached
  // — that is what the "No Estimate Linked" modal at the bottom of this file renders for (1003 of
  // 1313 cube-qa contractsVersions carry no such embed). Selected at the type it really has so the
  // guards below stay meaningful. SERVER SCHEMA GAP — the field should be `.optional()`.
  const linkedEstimateVersion = useSelector(
    (state: RootState): RootState["contractDetails"]["linkedEstimateVersion"] | undefined =>
      state.contractDetails.linkedEstimateVersion,
  );
  const estimateId = linkedEstimateVersion?.estimateId;
  const estimateVersionId = linkedEstimateVersion?.versionId;
  const contractId = params.contractId || props.contractId;
  const [currencyData, setCurrencyData] = useState<ProjectCurrencyState>({});
  const [linkBoqItemBtnState, setLinkBoqItemBtnState] = useState(false);
  const [customBoqQuantity, setCustomBoqQuantity] = useState<Record<string, number>>({});
  const [autoPopulateState, setAutoPopulateState] = useState(false);

  const setInitialData = useCallback(async () => {
    const response = await getDefaultCurrencyDetailForProjectLevel({
      projectId: params.projectId!,
    });
    if (response.data.status) {
      setCurrencyData({
        currencyDetail: response.data.currencyDetail,
        symbolDetail: response.data.symbolDetail,
      });
    }
  }, [params.projectId]);

  useEffect(() => {
    setInitialData();
  }, [setInitialData]);

  const handleLinkBoqItemsClick = async () => {
    // Non-null on the ref: the Link button that calls this renders in the modal footer alongside the
    // grid, so the ref is attached before it can fire. Erased at emit — an unattached ref still
    // throws exactly as it does today.
    // The dataView pushes `getItemById(id)` for every id still in `selectedRowIds`, which is
    // `undefined` for a selection kept across a refresh or a filter change — so `getAllSelectedItems()`
    // is declared with the holes in it. They are dropped here alongside the dummy rows.
    const selectedItems: Array<BoqSelectionRow | undefined> = boqItemsRef.current!.dataView.getAllSelectedItems();
    const selectedBoqData = selectedItems.filter(
      (record): record is BoqSelectionRow => record !== undefined && !record.__isDummy,
    );
    const isAlreadyAdded = selectedBoqData.some(
      (data) => data.isAlreadyAddedInThisContract || data.isAlreadyAddedInThisForm || data.isAlreadyAddedInThisTask,
    );
    const boqItems = selectedBoqData.map((data) => {
      const quantity = autoPopulateState ? data.availableQuantity : (customBoqQuantity[data.itemId] ?? 0);

      return {
        customQuantity: quantity,
        itemId: data.itemId,
        parentsList: data.parentsList,
      };
    });
    if (isAlreadyAdded) {
      dispatch(
        showToastNotification({
          content: t("One of the selected Item(s) is already added"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (!boqItems.length) {
      dispatch(
        showToastNotification({
          content: t("No BOQ Items have been selected"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (props.source == "gantt") {
      props.addBoqItemsCallBack({ boqItems });
    } else {
      // `source` is `"gantt" | "forms"`, so this is the forms caller.
      props.addBoqItemsCallBack({ boqItems, autoPopulateState });
    }
  };

  const handleCancelBoqClick = () => {
    props.setShowBoqModal(false);
  };

  const boqHeaderContent = () => (
    <div className="  w-full flex justify-between rounded-md">
      <div className="">{t("BOQ Items")}</div>
    </div>
  );

  const setBtnState = (state: boolean) => {
    setLinkBoqItemBtnState(state);
  };

  const getCustomBoqQuantity = ({ itemId, value }: { itemId: string; value: number }) => {
    setCustomBoqQuantity((prev) => ({ ...prev, [itemId]: value }));
  };

  const boqModelContent = useCallback(
    () =>
      currencyData.currencyDetail && (
        <div className="mb-6 ms-6 me-6 flex overflow-hidden h-full">
          <EstimateCostDatabaseItems
            contractId={contractId}
            contractVersionId={contractDetails.versionId}
            estimateVersionId={estimateVersionId}
            estimateId={estimateId}
            getCustomBoqQuantity={getCustomBoqQuantity}
            setBtnState={setBtnState}
            ref={boqItemsRef}
            enableCheckboxSelector
            source={itemListSources.LINK_BOQ_ITEMS}
            // Built at the shape the prop declares (`EstimateCurrency`), the same way the sibling
            // Tasks section builds it. This used to supply `_id` (never a key of `currencyDetail`,
            // which declares `id`) and `name`, so `currencyData.id` reached the grid as `undefined`
            // and `selectedSymbol` as `undefined` whenever the response carried no `symbolDetail` —
            // and the grid reads `currencyData.selectedSymbol.name` unconditionally.
            currencyData={{
              id: currencyData.currencyDetail.id,
              selectedSymbol: { name: currencyData.symbolDetail?.name ?? "" },
            }}
            childParentMapper={{
              itemId: "itemId",
              parentId: "parentId",
            }}
            taskLinking={props.source == "gantt"}
            formLinking={props.source == "forms"}
            taskId={props.selectedTask?.originalTaskId}
            formId={props.formId}
            setAutoPopulateState={setAutoPopulateState}
          />
        </div>
      ),

    [
      currencyData,
      linkBoqItemBtnState,
      customBoqQuantity,
      contractDetails.versionId,
      contractId,
      estimateId,
      estimateVersionId,
      props.formId,
      props.selectedTask?.originalTaskId,
      props.source,
    ],
  );

  const boqFooterContent = () => (
    <div className=" flex gap-4">
      <ButtonComponent
        disabled={!linkBoqItemBtnState}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleLinkBoqItemsClick}
      >
        {t("Link")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancelBoqClick}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  const getBoqItemsList = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={boqFooterContent()}
      width="80%"
      height="80%"
      headerTitle={boqHeaderContent()}
      onCancel={handleCancelBoqClick}
      isVisible={props.showBoqModal}
      modelContent={boqModelContent()}
    />
  );

  const noEstimateLinkedWarningModal = () => (
    <ModalStateDialogComponent
      footerContent={<div />}
      width="30%"
      headerTitle={
        <div className="flex justify-between items-center w-full">
          <span>{t("No Estimate Linked")}</span>
        </div>
      }
      onCancel={handleCancelBoqClick}
      isVisible={props.showBoqModal}
      modelContent={<div className="">{t("No Estimate have been attached to this contract.")}</div>}
    />
  );

  return <div>{props.showBoqModal && estimateVersionId ? getBoqItemsList() : noEstimateLinkedWarningModal()}</div>;
}
export default LinkBoqToTaskAndForm;
